import {Box, Hero} from '@primer/react-brand'
import {ContentfulAppStoreButtonGroup} from '../ContentfulAppStoreButtonGroup/ContentfulAppStoreButtonGroup'
import {getPrimerIcon} from '../../../lib/utils/icons'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'
import {getImageSources, MAX_CONTENT_WIDTH} from '../../../lib/utils/images'
import styles from './ContentfulHero.module.css'
import {ContentfulAnimatedVideo} from '../ContentfulAnimatedVideo/ContentfulAnimatedVideo'
import {IframeVideo} from '../../IframeVideo/IframeVideo'
import type {ContentfulHeroProps} from './contentful-hero.types'

/**
 * Hero layout component used by both baseline and Ninetailed experiences.
 * Handles all the visual rendering logic for ContentfulHero.
 */
export function ContentfulHeroLayout({component, paddingBlockEnd, analyticsContext, ...props}: ContentfulHeroProps) {
  const {
    align,
    callToActionPrimary,
    callToActionPrimaryVariant,
    callToActionSecondary,
    description,
    descriptionVariant,
    heading,
    headingSize,
    image,
    imagePosition,
    label,
    labelColor,
    trailingComponent,
    videoSrc,
    animatedVideo,
  } = component.fields

  const isHeroImage = image && !videoSrc

  const hasTrailingComponent = trailingComponent !== undefined && trailingComponent.length > 0

  const Octicon = typeof label !== 'string' && getPrimerIcon(label?.fields.icon)

  const {heroClassName = 'pb-0'} = props

  return (
    <Box className={styles.contentfulHeroContainer} paddingBlockEnd={paddingBlockEnd || {narrow: 96, regular: 128}}>
      <Hero
        data-hpc={props['data-hpc']}
        align={align}
        trailingComponent={
          hasTrailingComponent
            ? () => (
                <ContentfulAppStoreButtonGroup
                  components={trailingComponent}
                  analyticsLabel="hero"
                  analyticsLocation="hero"
                />
              )
            : undefined
        }
        className={heroClassName}
      >
        {label &&
          (typeof label === 'string' ? (
            <Hero.Label {...(labelColor ? {color: labelColor} : {})}>{label}</Hero.Label>
          ) : (
            <Hero.Label
              color={label.fields.color}
              size={label.fields.size}
              // eslint-disable-next-line react-hooks/static-components
              {...(Octicon ? {leadingVisual: <Octicon />} : {})}
            >
              {label.fields.text}
            </Hero.Label>
          ))}

        <Hero.Heading size={headingSize}>{heading}</Hero.Heading>

        {description ? <Hero.Description variant={descriptionVariant}>{description}</Hero.Description> : null}

        {!hasTrailingComponent && callToActionPrimary ? (
          <Hero.PrimaryAction
            href={callToActionPrimary.fields.href}
            variant={callToActionPrimaryVariant || 'primary'}
            data-ref={`hero-primary-action-${callToActionPrimary.sys.id}`}
            {...getAnalyticsEvent(
              {
                action: callToActionPrimary.fields.text,
                tag: 'button',
                location: 'hero',
                context: analyticsContext ? `strategy:${analyticsContext}` : 'CTAs',
              },
              {context: false},
            )}
          >
            {callToActionPrimary.fields.text}
          </Hero.PrimaryAction>
        ) : null}

        {!hasTrailingComponent && callToActionSecondary ? (
          <Hero.SecondaryAction
            href={callToActionSecondary.fields.href}
            {...getAnalyticsEvent(
              {
                action: callToActionSecondary.fields.text,
                tag: 'button',
                location: 'hero',
                context: analyticsContext ? `strategy:${analyticsContext}` : 'CTAs',
              },
              {context: false},
            )}
          >
            {callToActionSecondary.fields.text}
          </Hero.SecondaryAction>
        ) : null}
        {isHeroImage ? (
          <Hero.Image
            as="picture"
            position={imagePosition === 'Inline' ? 'inline-end' : 'block-end'}
            src={`${image.fields.file.url}?fm=webp`}
            sources={getImageSources(image.fields.file.url, {maxWidth: MAX_CONTENT_WIDTH})}
            alt={image.fields.description || ''}
          />
        ) : null}
      </Hero>
      {videoSrc !== undefined && !isHeroImage ? (
        <IframeVideo videoSrc={videoSrc} heading={heading} image={image} className={styles.heroVideo} />
      ) : null}
      {!videoSrc && !image && animatedVideo && (
        <Box paddingBlockStart={80}>
          <ContentfulAnimatedVideo component={animatedVideo} analyticsLocation="hero" />
        </Box>
      )}
    </Box>
  )
}
